type TimelineHighlightRect = Pick<DOMRectReadOnly, 'x' | 'y' | 'width' | 'height'>;

export interface TimelineHighlightsGeometrySnapshot {
  readonly isFixedPaneCollapsed: boolean;
  readonly headerHeight: number;
  readonly rowHeight: number;
  readonly contentHeight: number;
  readonly pinnedWidth: number;
  readonly scrollingWidth: number;
  readonly fieldBounds: readonly Pick<TimelineHighlightRect, 'x' | 'width'>[];
  readonly visualRowIndexBySelectionRow: readonly number[];
}

/**
 * Publishes Timeline-owned DOM and immutable geometry to its layout-scoped
 * highlights controller. React owns registration only; bootstrap owns the
 * controller lifecycle.
 */
export interface TimelineHighlightsGeometryAdapter {
  getRoot(): HTMLElement | null;
  getPinnedRoot(): HTMLElement | null;
  getResizeObserveTarget(): HTMLElement | null;
  getSnapshot(): TimelineHighlightsGeometrySnapshot;
}

let currentAdapter: TimelineHighlightsGeometryAdapter | null = null;
const geometryChangeListeners = new Set<
  (adapter: TimelineHighlightsGeometryAdapter | null) => void
>();

function notifyGeometryChangeListeners(): void {
  for (const listener of geometryChangeListeners) {
    listener(currentAdapter);
  }
}

/**
 * Publishes the DOM-backed geometry owned by the mounted Timeline layout.
 * Replacing or removing the active adapter notifies the layout-scoped
 * highlights controller without giving React ownership of that controller.
 */
export function registerTimelineHighlightsGeometry(
  adapter: TimelineHighlightsGeometryAdapter
): () => void {
  currentAdapter = adapter;
  notifyGeometryChangeListeners();

  return () => {
    if (currentAdapter === adapter) {
      currentAdapter = null;
      notifyGeometryChangeListeners();
    }
  };
}

export function notifyTimelineHighlightsGeometryChange(
  adapter: TimelineHighlightsGeometryAdapter
): void {
  if (currentAdapter === adapter) {
    notifyGeometryChangeListeners();
  }
}

/**
 * Subscribes the Timeline highlights controller to geometry publication.
 * The current adapter is emitted immediately so either bootstrap or React may
 * initialize first. The returned function removes the controller-level listener.
 */
export function subscribeToTimelineHighlightsGeometryChanges(
  listener: (adapter: TimelineHighlightsGeometryAdapter | null) => void
): () => void {
  geometryChangeListeners.add(listener);
  listener(currentAdapter);

  return () => {
    geometryChangeListeners.delete(listener);
  };
}

/**
 * Maps selection-machine indexes onto Timeline's virtual grid without adding
 * Timeline DOM knowledge to the shared highlight renderer. It reads the active
 * adapter at call time so geometry can change without replacing the controller.
 */
export class TimelineHighlightsGeometryResolver {
  getScrollContainer(): HTMLElement | null {
    return currentAdapter?.getRoot() ?? null;
  }

  getCellBounds(rowIndex: number, colIndex: number): TimelineHighlightRect | null {
    const snapshot = currentAdapter?.getSnapshot();
    const row = this.#getRowBounds(rowIndex);
    const field = snapshot?.fieldBounds[colIndex];

    if (!snapshot || snapshot.isFixedPaneCollapsed || !row || !field) {
      return null;
    }

    return {
      x: field.x,
      y: row.y,
      width: field.width,
      height: row.height
    };
  }

  getRowBounds(rowIndex: number): TimelineHighlightRect | null {
    const row = this.#getRowBounds(rowIndex);
    const snapshot = currentAdapter?.getSnapshot();

    return row && snapshot
      ? {
          x: 0,
          y: row.y,
          width: snapshot.scrollingWidth,
          height: row.height
        }
      : null;
  }

  getColumnBounds(colIndex: number): TimelineHighlightRect | null {
    const snapshot = currentAdapter?.getSnapshot();
    const field = snapshot?.fieldBounds[colIndex];

    if (!snapshot || snapshot.isFixedPaneCollapsed || !field) {
      return null;
    }

    return {
      x: field.x,
      y: snapshot.headerHeight,
      width: field.width,
      height: Math.max(0, snapshot.contentHeight - snapshot.headerHeight)
    };
  }

  getContainerBounds(): TimelineHighlightRect | null {
    const snapshot = currentAdapter?.getSnapshot();

    if (!snapshot) {
      return null;
    }

    return {
      x: 0,
      y: snapshot.headerHeight,
      width: snapshot.scrollingWidth,
      height: Math.max(0, snapshot.contentHeight - snapshot.headerHeight)
    };
  }

  #getRowBounds(rowIndex: number): Pick<TimelineHighlightRect, 'y' | 'height'> | null {
    const snapshot = currentAdapter?.getSnapshot();
    const visualRowIndex = snapshot?.visualRowIndexBySelectionRow[rowIndex];

    if (snapshot === undefined || visualRowIndex === undefined || visualRowIndex < 0) {
      return null;
    }

    return {
      y: snapshot.headerHeight + visualRowIndex * snapshot.rowHeight,
      height: snapshot.rowHeight
    };
  }
}

/** Resolves the same logical ranges inside Timeline's horizontally fixed pane. */
export class TimelinePinnedHighlightsGeometryResolver extends TimelineHighlightsGeometryResolver {
  override getScrollContainer(): HTMLElement | null {
    return currentAdapter?.getPinnedRoot() ?? null;
  }

  override getRowBounds(rowIndex: number): TimelineHighlightRect | null {
    const bounds = super.getRowBounds(rowIndex);
    const snapshot = currentAdapter?.getSnapshot();

    return bounds && snapshot ? { ...bounds, width: snapshot.pinnedWidth } : null;
  }

  override getContainerBounds(): TimelineHighlightRect | null {
    const bounds = super.getContainerBounds();
    const snapshot = currentAdapter?.getSnapshot();

    return bounds && snapshot ? { ...bounds, width: snapshot.pinnedWidth } : null;
  }
}

export function getTimelinePinnedHighlightColumnCount(): number {
  const snapshot = currentAdapter?.getSnapshot();

  return snapshot && !snapshot.isFixedPaneCollapsed && snapshot.pinnedWidth > 0
    ? Math.max(1, snapshot.fieldBounds.length)
    : 0;
}
